/* eslint-disable react/require-default-props */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { getNonCompletionReasons } from '../../services/routing/routingApi';

// =============================================================================
// NonCompletionReasonPicker
//
// Dropdown reaproveitável para selecionar motivos de não realização. Originalmente
// embutido em StatusChangeModal — extraído pra também ser usado pela UI v1 da
// Roteirização (StopDrawer / EventConfirmDialog) ao registrar evento CANCELLED.
//
// Faz fetch interno via getNonCompletionReasons (GET /routing/non-completion-reasons).
// Mantém cache module-level pra evitar refetch quando múltiplas instâncias
// coexistem na mesma navegação. O cache é resetado em reload de página.
//
// Uso:
//   <NonCompletionReasonPicker
//      value={reasonId}
//      onChange={setReasonId}
//      labelText="Motivo de não realização"
//      required
//   />
// =============================================================================

let cachedReasons = null;
let inFlightPromise = null;

function fetchReasonsCached() {
   if (cachedReasons) return Promise.resolve(cachedReasons);
   if (inFlightPromise) return inFlightPromise;
   inFlightPromise = getNonCompletionReasons()
      .then((list) => {
         cachedReasons = Array.isArray(list) ? list : [];
         return cachedReasons;
      })
      .finally(() => {
         inFlightPromise = null;
      });
   return inFlightPromise;
}

const Wrap = styled.div`
   display: flex;
   flex-direction: column;
   gap: 4px;
`;

const Label = styled.label`
   font-size: 0.75rem;
   font-weight: 600;
   color: ${({ theme }) => theme?.colors?.textSecondary ?? '#555'};
`;

const Select = styled.select`
   padding: 8px 10px;
   font-size: 0.85rem;
   border-radius: 8px;
   border: 1px solid
      ${({ theme }) => theme?.colors?.border ?? 'rgba(0,0,0,0.15)'};
`;

const EmptyHint = styled.span`
   font-size: 0.7rem;
   color: #c62828;
   margin-top: 4px;
`;

export default function NonCompletionReasonPicker({
   value = '',
   onChange,
   disabled = false,
   required = false,
   id = undefined,
   labelText = undefined,
}) {
   const [reasons, setReasons] = useState(cachedReasons || []);
   const [isLoading, setIsLoading] = useState(!cachedReasons);

   useEffect(() => {
      // Cache hit: já temos a lista; render direto.
      if (cachedReasons) {
         setReasons(cachedReasons);
         setIsLoading(false);
         return undefined;
      }

      let cancelled = false;
      setIsLoading(true);

      fetchReasonsCached()
         .then((list) => {
            if (cancelled) return;
            setReasons(list);
         })
         .catch(() => {
            if (cancelled) return;
            // Falha silenciosa — usuário ainda pode digitar o motivo em texto
            // livre se a UI consumidora oferecer fallback.
            setReasons([]);
         })
         .finally(() => {
            if (!cancelled) setIsLoading(false);
         });

      return () => {
         cancelled = true;
      };
   }, []);

   return (
      <Wrap>
         {labelText && (
            <Label htmlFor={id}>
               {labelText}
               {required ? ' *' : ''}
            </Label>
         )}
         <Select
            id={id}
            value={value ?? ''}
            onChange={(event) => onChange(event.target.value)}
            disabled={disabled || isLoading}
         >
            <option value="">
               {isLoading ? 'Carregando…' : '— selecione —'}
            </option>
            {reasons.map((reason) => (
               <option key={reason.id} value={reason.id}>
                  {reason.description}
               </option>
            ))}
         </Select>
         {!isLoading && reasons.length === 0 && (
            <EmptyHint>
               Nenhum motivo cadastrado. Cadastre em Roteirização &gt; Cadastros.
            </EmptyHint>
         )}
      </Wrap>
   );
}

NonCompletionReasonPicker.propTypes = {
   value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
   onChange: PropTypes.func.isRequired,
   disabled: PropTypes.bool,
   required: PropTypes.bool,
   id: PropTypes.string,
   labelText: PropTypes.string,
};
